import { useEffect, useRef } from 'react'

/**
 * What the host publishes while the measured element is not on screen.
 *
 * `publish-zero` for a variable every consumer subtracts — absence genuinely means "no height", and
 * a missing property would make each `calc()` collapse to nothing. `remove-property` for a variable
 * whose consumers carry their own fallback, so the next render without the element falls back
 * rather than inheriting a stale value.
 */
export type AbsentTargetPolicy = 'publish-zero' | 'remove-property'

/**
 * Publishes a target element's live height as a CSS custom property on a host element, so
 * descendants that cannot see the target's box — a `position: fixed` layer, or a page in a
 * different part of the tree — can size themselves against it.
 *
 * Measuring rather than reproducing the number in SCSS is the point: a bar or header whose height
 * depends on wrapped copy, a breakpoint, or `env(safe-area-inset-bottom)` has no value a stylesheet
 * can name, and a hard-coded guess is wrong on exactly the devices the inset exists for.
 *
 * `ResizeObserver` rather than a one-shot read, because the target reflows after mount — copy
 * wrapping, rotation, a font-size change. That also covers a height change with no remount, so the
 * effect only needs re-keying on whether the target is rendered at all: a ref landing is not a
 * render the effect can see, and the flag flips in the same commit that mounts or unmounts it.
 *
 * Border-box throughout: padding is where a safe-area inset lives, and a consumer reserving the
 * space has to clear that too.
 */
export function usePublishedElementHeight(
  cssVariableName: string,
  isTargetRendered: boolean,
  { whenTargetAbsent }: { whenTargetAbsent: AbsentTargetPolicy },
) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const targetRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined

    const target = targetRef.current
    if (!target) {
      if (whenTargetAbsent === 'publish-zero') host.style.setProperty(cssVariableName, '0px')
      return undefined
    }

    const publish = (height: number) => host.style.setProperty(cssVariableName, `${height}px`)
    publish(target.getBoundingClientRect().height)

    // Not available in jsdom, where every element measures 0 anyway — the one-shot read above is
    // all that surface can offer.
    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return
      const borderBox = entry.borderBoxSize[0]
      publish(borderBox ? borderBox.blockSize : entry.target.getBoundingClientRect().height)
    })
    observer.observe(target)

    return () => {
      observer.disconnect()
      if (whenTargetAbsent === 'remove-property') host.style.removeProperty(cssVariableName)
    }
  }, [cssVariableName, isTargetRendered, whenTargetAbsent])

  return { hostRef, targetRef }
}
